import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { resolveOpenPlacementPetIds, type FosterHome, type FosterPlacementRecord } from '../lib/fosterAdapters';
import { rescuePetQuery, type RescuePetData, type RescuePlacementPetGql } from '../lib/rescueQueries';
import { useToast } from '../components/Toast/ToastContext';
import { trackFosterRosterViewed, trackPetViewed } from '../analytics';
import useFosterHomes from './useFosterHomes';

interface UseFosterRosterOptions {
  /**
   * Whether to fetch the roster at all. It backs the assign-to-foster picker as well as the roster
   * section, so it stays on for tabs that only need the picker.
   */
  enabled: boolean;
  /** Whether the roster section is the tab on screen, for the once-per-visit analytics event. */
  visible: boolean;
}

/**
 * The Fosters tab's roster half: the saved homes, the details overlay, and the pet behind a
 * placement. Extracted from the Devices page, which is long enough that this sitting inline made the
 * roster's own coupling — a held home id against a modal's reset, an overlay against a URL change —
 * hard to see.
 */
export default function useFosterRoster({ enabled, visible }: UseFosterRosterOptions) {
  const homes = useFosterHomes({ skip: !enabled });

  // Held by id, not value: every mutation refetches the roster, and a captured object would keep
  // showing stale placements, readiness pills and archive-ability after that.
  const [detailHomeId, setDetailHomeId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [placementPet, setPlacementPet] = useState<RescuePlacementPetGql | null>(null);
  // The row that was clicked, kept as a fallback. The roster table renders a server-chosen page
  // from its own query now, so a row can be on screen before — or without — this full list
  // answering, and resolving by id alone would open the modal on nothing at all: it renders null
  // without a home, so View would simply do nothing. The lookup still wins whenever it succeeds,
  // which is the case the id-not-value rule above is about.
  const [clickedHome, setClickedHome] = useState<FosterHome | null>(null);
  // The placement whose pet is being fetched, so the row can say it is working. Also what stops a
  // second click on the same row starting a second request.
  const [loadingPlacementId, setLoadingPlacementId] = useState<string | null>(null);
  const detailHome =
    homes.homes.find((home) => home.id === detailHomeId) ?? (clickedHome?.id === detailHomeId ? clickedHome : null);

  /**
   * Which placements link to a pet, answered from the home alone.
   *
   * This used to drain every IN_FOSTER page to hold the pet records, and was gated on the tab being
   * up because the drain was too expensive to run per overlay. None of that is needed: the home
   * already carries `placedPets`, which is the only thing the name match consults. See
   * {@link resolveOpenPlacementPetIds} for the rules.
   */
  const placementPetIds = useMemo(
    () => (detailHome ? resolveOpenPlacementPetIds(detailHome) : new Map<string, string>()),
    [detailHome],
  );

  const viewablePlacementIds = useMemo(() => new Set(placementPetIds.keys()), [placementPetIds]);

  // The pet itself, fetched when a row is actually clicked rather than held for every row in case
  // one is.
  //
  // `client.query` rather than `useLazyQuery`: the latter leaves a watched query behind once it has
  // run, pinned to whichever pet was viewed last. Every mutation on this page refetches active
  // queries, so that pet would be re-fetched by all of them for the rest of the page's life — and
  // once it is out of reach, as it is after an adoption completes, each of those is a rejection
  // nothing is waiting on. A one-off read never joins `active`.
  const apolloClient = useApolloClient();
  const { showToast } = useToast();

  // Which click a response belongs to. The lookup used to be synchronous, so a stale one could not
  // exist; now the overlay can be dismissed — the page navigates under it, see `dismissOverlays` —
  // or a second row clicked, while the first request is still out. Every path that invalidates a
  // pending open bumps this, and a response whose token has moved on is dropped.
  const placementPetRequest = useRef(0);
  const cancelPendingPlacementPet = useCallback(() => {
    placementPetRequest.current += 1;
  }, []);
  // Unmounting is one more way to leave a pending open behind — navigating off the page while the
  // fetch is out — and the only one with no handler of its own to bump the token.
  useEffect(() => cancelPendingPlacementPet, [cancelPendingPlacementPet]);

  /**
   * Once per visit, and only once the roster is an answer. Keyed on `loaded` rather than `!loading`
   * and guarded on `error`: both a skipped and a failed query report not-loading with no homes, and
   * reporting those as `home_count: 0` would mix "this rescue has no saved homes" into the same
   * number as "we never found out" — which is the metric sizing the roster.
   *
   * The ref resets on leaving so a return visit counts again, while the refetch every foster
   * mutation triggers does not. A failed load leaves it unset, so a successful retry still counts.
   */
  const viewTracked = useRef(false);
  useEffect(() => {
    if (!visible) {
      viewTracked.current = false;
      return;
    }
    if (!homes.loaded || homes.error || viewTracked.current) {
      return;
    }
    viewTracked.current = true;
    trackFosterRosterViewed(homes.homes.length);
  }, [visible, homes.loaded, homes.error, homes.homes.length]);

  function openDetail(home: FosterHome) {
    setDetailHomeId(home.id);
    setClickedHome(home);
    setDetailOpen(true);
  }

  // Stable, so callers can name it as an effect dependency rather than suppressing the lint rule.
  const closeDetail = useCallback(() => {
    cancelPendingPlacementPet();
    setLoadingPlacementId(null);
    setDetailOpen(false);
    setDetailHomeId(null);
    setClickedHome(null);
  }, [cancelPendingPlacementPet]);

  /**
   * Opens over the details modal rather than replacing it, so dismissing the pet lands back on the
   * home its placement belongs to.
   */
  async function viewPlacementPet(placement: FosterPlacementRecord) {
    const petId = placementPetIds.get(placement.id);
    const home = detailHome;
    if (!petId || !home || loadingPlacementId) {
      return;
    }
    const request = ++placementPetRequest.current;
    setLoadingPlacementId(placement.id);

    let data: RescuePetData | undefined;
    try {
      ({ data } = await apolloClient.query<RescuePetData>({
        query: rescuePetQuery,
        variables: { petId },
        fetchPolicy: 'cache-first',
      }));
    } catch {
      // `client.query` rejects where the lazy hook resolved with an error, and a pet out of reach
      // is the ordinary case here rather than an exceptional one. Handled below, alongside a
      // response that came back without one.
      data = undefined;
    }

    if (request !== placementPetRequest.current) {
      // The overlay was dismissed, another row was clicked, or the page unmounted while this was in
      // flight. Opening now would put the modal back over whatever the user moved on to.
      return;
    }
    setLoadingPlacementId(null);

    const pet = data?.rescuePet?.pet;
    if (!pet) {
      // `rescuePet` throws rather than returning null for a pet that has moved out of reach, so
      // this is the ordinary shape of "gone since the roster loaded". The row was rendered as a
      // link, so silence would read as a dead click.
      showToast('Could not open that pet.');
      return;
    }

    // The name match picked this pet out of the household; the transfer says whether the rescue
    // actually placed it there. A ledger row can outlive the stay it records, and a foster's own
    // pet can share a name with one the rescue placed — together those open a pet that was never
    // this rescue's to look at.
    const placedHere =
      data?.rescuePet?.transfer?.type === 'FOSTER' &&
      data.rescuePet.transfer.toHousehold?.id === home.fosterHouseholdId;
    if (!placedHere) {
      showToast('That stay is no longer open for this foster.');
      return;
    }

    // Same event the Dashboard fires, so 'Pet Viewed' stays whole across the portal's entry points.
    trackPetViewed(!!pet.chip, !!pet.device, 'foster', pet.species ?? undefined);
    setPlacementPet(pet);
  }

  /**
   * Drop every roster overlay. Used when the page navigates under them: a pet profile's device card
   * links back with a search on it, and the row it lands on would otherwise sit behind the modal
   * that opened it.
   */
  const dismissOverlays = useCallback(() => {
    cancelPendingPlacementPet();
    setLoadingPlacementId(null);
    setPlacementPet(null);
    closeDetail();
  }, [cancelPendingPlacementPet, closeDetail]);

  const closePlacementPet = useCallback(() => {
    cancelPendingPlacementPet();
    setLoadingPlacementId(null);
    setPlacementPet(null);
  }, [cancelPendingPlacementPet]);

  return {
    homes,
    detailHome,
    detailOpen,
    openDetail,
    closeDetail,
    addOpen,
    setAddOpen,
    placementPet,
    closePlacementPet,
    viewablePlacementIds,
    viewPlacementPet,
    loadingPlacementId,
    dismissOverlays,
  };
}
